/*
 * Explicit reply sending (docs/2026-09-20-send-on-approve-ui-shape.md). Available for a reply step
 * after membership and reply-agent access have been resolved.
 *
 * A send takes two deliberate actions. The send button opens a confirmation panel, `createSendConfirm`
 * (src/send-confirm.ts), which states in plain words what is about to leave and confirms nothing
 * until it has been open long enough that a double click cannot have been the second action. Only
 * its confirm control starts the hold-then-queue sequence, `createSendHold` (src/send-approve.ts):
 * approval is recorded then, and the queue command is issued only when the 10-second countdown
 * completes, so Undo or leaving the page sends nothing either. The server-side gate is untouched
 * by both: this is a guard in front of it, not a replacement for any part of it.
 */
import { useEffect, useId, useMemo, useRef, useState } from "react";
import {
  approvalIsCurrent,
  type PlanStep,
  type WorkPlan,
} from "../../../skills/leadcue-reply/scripts/lib/work-plan.ts";
import type { DraftRevision, ThreadSnapshot } from "../src/mail-content.ts";
import type { PlanCommand, PlanCommandOutcome } from "../src/plan-command.ts";
import { createSendHold, type SendPhase, type SendStrip, sendStrip } from "../src/send-approve.ts";
import {
  confirmKeyAction,
  createSendConfirm,
  type SendConfirmState,
  sendSubject,
} from "../src/send-confirm.ts";
import { useDraftMaterialsForSend } from "./draft-materials.tsx";
import { CheckIcon, ExternalLinkIcon, SendIcon } from "./icons.tsx";
import { fileSize } from "./material-request-sheet.tsx";

type Props = {
  plan: WorkPlan;
  step: PlanStep;
  thread: ThreadSnapshot | null;
  drafts: readonly DraftRevision[];
  /** Busy, editing, an open question or a draft answering an older message: nothing to approve yet. */
  blocked: boolean;
  isLead: boolean;
  /** Issues one plan command and says whether it applied, was refused, or is unknown. */
  issue: (command: PlanCommand) => Promise<PlanCommandOutcome>;
  readPlan: () => Promise<WorkPlan>;
  refresh: () => Promise<void>;
  approveOnly: () => void;
  onSending: (sending: boolean) => void;
  whenLabel: (iso: string) => string;
};

const NOTE: Record<Extract<SendPhase, { kind: "cancelled" }>["reason"], string> = {
  undo: "Undone. Nothing was sent.",
  stale: "Held. The conversation changed, so nothing was sent. Read the new message first.",
  approve_failed: "Could not approve. Nothing was sent.",
  approve_unknown: "The approval could not be confirmed. Nothing was sent.",
};

/** Backing out of the confirmation is ordinary and says so quietly; the conversation moving is not. */
const CONFIRM_NOTE = {
  cancel: "Cancelled. Nothing was sent.",
  stale: NOTE.stale,
} as const;

/** What the strip says while the send cannot be offered, by why. */
const STRIP_NOTICE: Record<Exclude<SendStrip["kind"], "ready">, string> = {
  checking: "Checking the draft's files…",
  waiting_for_file: "Waiting for the draft's file. Upload it above before sending.",
  setting_removed:
    "A link in this draft was removed from the campaign. Redraft it above before sending.",
  unresolvable: "Can't tell who this goes to. Send it from Bison.",
};

/** What the send button says, and the warning under it, once a queue was refused or is unclear. */
const RETRY_LABEL: Partial<Record<SendPhase["kind"], string>> = {
  queue_refused: "Try send again",
  queue_unknown: "Check again",
};
const QUEUE_WARNING: Partial<Record<SendPhase["kind"], string>> = {
  queue_refused: "Approved, not sent. LeadCue refused to queue this reply.",
  queue_unknown:
    "Approved, but LeadCue could not confirm it was queued, so it may still send. Check again asks about this same send; it cannot go out twice.",
};

/** One command against the step, then the card drawn again from what the server holds. */
async function issued(
  props: Props,
  refresh: "always" | "applied",
  command: PlanCommand,
): Promise<PlanCommandOutcome> {
  const outcome = await props.issue(command);
  if (refresh === "always" || outcome.kind === "applied")
    await props.refresh().catch(() => undefined);
  return outcome;
}

/** The hold for one send intent, reading the newest props through `latest` (`createSendHold`). */
function sendHold(
  latest: { readonly current: Props },
  operation: { readonly current: string },
  onPhase: (phase: SendPhase) => void,
  needsApproval: boolean,
) {
  const stepId = latest.current.step.id;
  return createSendHold({
    // Whatever an approval's answer, the card is drawn again: unclear, it may still be recorded.
    approve: needsApproval
      ? () =>
          issued(latest.current, "always", {
            stepId,
            action: "approve",
            commandId: crypto.randomUUID(),
            expectedRevision: latest.current.plan.revision,
          })
      : null,
    // Read fresh from the server: only an approved, current step may be queued, at its own revision.
    current: async () => {
      const fresh = await latest.current.readPlan();
      const candidate = fresh.steps.find((entry) => entry.id === stepId);
      return candidate && candidate.state === "approved" && approvalIsCurrent(fresh, candidate)
        ? { revision: fresh.revision }
        : null;
    },
    execute: (revision) =>
      issued(latest.current, "applied", {
        stepId,
        action: "execute",
        commandId: operation.current,
        expectedRevision: revision,
      }),
    onPhase,
  });
}

export function SendControls(props: Props) {
  const { plan, step, thread, drafts, blocked, isLead, whenLabel } = props;
  const [phase, setPhase] = useState<SendPhase>({ kind: "idle" });
  const [confirming, setConfirming] = useState<SendConfirmState>({ kind: "closed" });
  const [backedOut, setBackedOut] = useState<keyof typeof CONFIRM_NOTE | null>(null);
  const latest = useRef(props);
  latest.current = props;
  // One operation id per send intent, kept across "Try send again" so a retry queues once.
  const operation = useRef(crypto.randomUUID());
  const hold = useRef<ReturnType<typeof createSendHold> | null>(null);
  const stepId = step.id;
  const approved = step.state === "approved" && approvalIsCurrent(plan, step);
  const materials = useDraftMaterialsForSend(
    plan.id,
    step.input.kind === "reply" ? step.input.draftRevision : -1,
  );
  const strip = sendStrip(thread, drafts, step, materials);

  /*
   * What this send would do, recomputed every render. The confirmation opens on one of these and
   * confirms only against the same one, so anything that moves underneath an open panel — a newer
   * reply, an edited draft, an approval gone stale, the step blocked — closes it and sends nothing.
   */
  const subject = sendSubject({ strip, step, planRevision: plan.revision, approved, blocked });

  /*
   * The gate is made once and outlives every render, so `proceed` reaches the current one through a
   * ref: a closure captured on the first render would send against that render's conversation.
   * Everything else the gate needs is passed in from the render that calls it.
   */
  const startNow = useRef<() => void>(() => undefined);
  const confirm = useRef<ReturnType<typeof createSendConfirm> | null>(null);
  /*
   * Built on demand rather than once, so a press always reaches a live gate. Unmounting disposes
   * this one and clears the ref (below); a disposed gate refuses every press for good, and React
   * can remount effects without re-rendering, so a press could otherwise land on a dead gate and
   * do nothing at all. Reading it through here is what makes that unreachable.
   */
  const gateNow = (): ReturnType<typeof createSendConfirm> => {
    confirm.current ??= createSendConfirm({
      proceed: () => startNow.current(),
      onState: setConfirming,
      onCancelled: setBackedOut,
    });
    return confirm.current;
  };
  const gate = gateNow();
  // A re-render is the only notice the gate gets that the conversation moved.
  useEffect(() => {
    gate.sync(subject);
  }, [gate, subject]);

  /*
   * Leaving the page (or another conversation) mid-countdown: execute never fires, and an open
   * confirmation goes with it rather than surviving into whatever is shown next.
   *
   * Both refs are cleared, not just disposed. A disposed gate refuses every press for good, so one
   * left in a ref that outlives its own cleanup — which is what React does when effects are
   * remounted, in StrictMode and under Activity — would leave a send button that silently does
   * nothing. Clearing them means the next render builds a live pair instead.
   */
  useEffect(
    () => () => {
      hold.current?.dispose();
      hold.current = null;
      confirm.current?.dispose();
      confirm.current = null;
    },
    [],
  );

  const panel = useRef<HTMLElement | null>(null);
  const headingId = useId();
  const open = confirming.kind === "open";
  /*
   * Focus lands on the panel, never on a control inside it. So Escape reaches the panel's own
   * handler, and an Enter held down from before it opened reaches nothing at all: confirming takes
   * moving focus to the confirm control first.
   */
  useEffect(() => {
    if (open) panel.current?.focus();
  }, [open]);
  /*
   * A press anywhere outside the panel backs out. That includes the second press of a double click
   * that lands wide of it, which is the gesture this whole step exists for.
   */
  useEffect(() => {
    if (!open) return;
    const away = (event: PointerEvent): void => {
      const target = event.target;
      if (target instanceof Node && panel.current?.contains(target)) return;
      gate.cancel("cancel");
    };
    document.addEventListener("pointerdown", away, true);
    return () => document.removeEventListener("pointerdown", away, true);
  }, [open, gate]);
  const active = ["approving", "holding", "queueing", "queued"].includes(phase.kind);
  const onSending = props.onSending;
  useEffect(() => {
    onSending(active);
    return () => onSending(false);
  }, [active, onSending]);

  /** Reached only from the confirmation's confirm control. Nothing else in here calls it. */
  const start = (): void => {
    const running = hold.current;
    // A refused or unclear queue is asked again under the same operation id, never a fresh one.
    if (running && RETRY_LABEL[running.phase().kind]) {
      void running.retry();
      return;
    }
    if (running && !["idle", "cancelled"].includes(running.phase().kind)) return;
    running?.dispose();
    operation.current = crypto.randomUUID();
    hold.current = sendHold(latest, operation, setPhase, !approved);
    void hold.current.press();
  };
  startNow.current = start;
  /*
   * Every press of the send button lands here, including the keyboard's Shift + A and the retry
   * after a failed queue: each of them can send, so each takes the same second action.
   */
  const press = (): void => {
    setBackedOut(null);
    gateNow().open(subject);
  };
  const sentAt = useMemo(() => {
    const proof = plan.history.findLast(
      (entry) => entry.stepId === stepId && entry.evidence.kind === "provider_receipt",
    );
    return proof?.evidence.kind === "provider_receipt" ? proof.evidence.observedAt : null;
  }, [plan.history, stepId]);
  const bisonUrl = thread?.bisonUrl ?? null;
  const openBison = bisonUrl ? (
    <a href={bisonUrl} target="_blank" rel="noreferrer">
      <ExternalLinkIcon size={16} /> Open in Bison
    </a>
  ) : null;

  if (step.state === "queued" || step.state === "executing")
    return (
      <p className="lcr-send-status" role="status">
        Sending…
      </p>
    );
  if (step.state === "completed")
    return sentAt ? (
      <div className="lcr-send-status lcr-send-done">
        <p className="lcr-send-line">
          <CheckIcon size={16} /> Sent · {whenLabel(sentAt)}
          {step.input.kind === "reply" ? ` · Draft r${step.input.draftRevision}` : ""}
        </p>
        {openBison}
      </div>
    ) : null;
  if (step.state === "uncertain")
    return (
      <div className="lcr-send-status lcr-send-uncertain" role="alert">
        <p className="lcr-send-line">
          <strong>Check Bison, don&apos;t resend.</strong> Replies lost track of whether this reply
          went out. Look in the Sent folder before doing anything else.
        </p>
        {openBison}
      </div>
    );
  if (!["pending", "invalidated", "approved"].includes(step.state)) return null;
  if (step.state === "approved" && !approved) return null;

  const busy = phase.kind === "approving" || phase.kind === "holding" || phase.kind === "queueing";
  const note =
    phase.kind === "cancelled" ? NOTE[phase.reason] : backedOut ? CONFIRM_NOTE[backedOut] : "";

  /*
   * The confirmation stands in for the strip and the buttons rather than covering them: the draft
   * above stays readable while it is open, which is the thing being confirmed. It is not a modal —
   * nothing is trapped, and the page behind it is neither dimmed nor frozen.
   *
   * Its confirm control is last, after a summary tall enough that it cannot land where the press
   * that opened the panel did. That is the second defence; the first is that the gate confirms
   * nothing at all for the first fraction of a second, whatever the layout puts under the pointer.
   */
  if (open && strip.kind === "ready")
    return (
      <div className="lcr-send">
        <section
          className="lcr-send-confirm"
          ref={panel}
          tabIndex={-1}
          aria-labelledby={headingId}
          onKeyDown={(event) => {
            if (confirmKeyAction(event.nativeEvent) !== "cancel") return;
            // The workspace's own Escape leaves the conversation; here it belongs to the panel.
            event.preventDefault();
            event.stopPropagation();
            gateNow().cancel("cancel");
          }}
        >
          <h3 className="lcr-send-confirm-title" id={headingId}>
            Send this reply?
          </h3>
          <dl className="lcr-send-confirm-facts">
            <dt>To</dt>
            <dd>{strip.to}</dd>
            <dt>From</dt>
            <dd>{strip.from}</dd>
            {strip.subject !== "" && (
              <>
                <dt>Subject</dt>
                <dd>{strip.subject}</dd>
              </>
            )}
            {strip.files.length > 0 && (
              <>
                <dt>Attached</dt>
                <dd>
                  {strip.files
                    .map((file) => `${file.fileName} (${fileSize(file.bytes)})`)
                    .join(", ")}
                </dd>
              </>
            )}
          </dl>
          <p className="lcr-send-line">
            Draft r{strip.draftRevision}
            {strip.files.length > 0
              ? strip.files.length === 1
                ? " and its file go"
                : " and its files go"
              : " goes"}{" "}
            out through Bison exactly as written above. A sent email can&apos;t be recalled.
          </p>
          <div className="lcr-send-confirm-actions">
            <button
              type="button"
              className="lcr-send-button"
              onClick={() => gateNow().cancel("cancel")}
            >
              Cancel
            </button>
            <button
              type="button"
              className="lcr-primary lcr-send-button lcr-send-confirm-go"
              onClick={() => gateNow().confirm(subject)}
            >
              <SendIcon size={16} /> Send reply
            </button>
          </div>
          <p className="lcr-send-confirm-back">You still get 10 seconds to undo. Esc cancels.</p>
        </section>
      </div>
    );

  return (
    <div className="lcr-send">
      {strip.kind === "ready" ? (
        <section className="lcr-send-strip" aria-label="What sending does">
          <p className="lcr-send-line">
            Sends from <strong>{strip.from}</strong> to <strong>{strip.to}</strong> through Bison.
            Draft r{strip.draftRevision}. It goes out exactly as shown above, and a sent email
            can&apos;t be recalled.
          </p>
          {strip.files.length > 0 && (
            <p className="lcr-send-line">
              Attaches{" "}
              {strip.files.map((file) => `${file.fileName} (${fileSize(file.bytes)})`).join(", ")}.
            </p>
          )}
          {strip.wordingWithoutFile && (
            <p className="lcr-send-line lcr-send-warn">
              This draft says a file is attached, but it carries none, so Replies won&apos;t send
              it. Take that wording out, or approve it and send it from Bison with the file.
            </p>
          )}
        </section>
      ) : (
        <section className="lcr-send-strip" aria-label="What sending does">
          <p className="lcr-send-line">{STRIP_NOTICE[strip.kind]}</p>
        </section>
      )}
      {phase.kind === "holding" ? (
        <div className="lcr-send-hold">
          <span aria-hidden="true">Sending in {phase.remaining} s</span>
          <span className="lcr-visually-hidden" role="status">
            Sending in {Math.max(1, Math.round(phase.remaining))} seconds. Undo is available.
          </span>
          <button type="button" className="lcr-send-button" onClick={() => hold.current?.undo()}>
            Undo
          </button>
        </div>
      ) : phase.kind === "queueing" ? (
        <p className="lcr-send-status" role="status">
          Sending…
        </p>
      ) : (
        <div className="lcr-send-actions">
          {strip.kind === "ready" && (
            <button
              type="button"
              className="lcr-primary lcr-send-button"
              data-shortcut="send"
              disabled={blocked || busy || strip.wordingWithoutFile}
              onClick={press}
            >
              <SendIcon size={16} />{" "}
              {RETRY_LABEL[phase.kind] ?? (approved ? "Send" : "Approve & send")}
              {isLead && <kbd className="lcr-kbd">Shift A</kbd>}
            </button>
          )}
          {!approved && (
            <button
              type="button"
              className="lcr-quiet lcr-send-button"
              data-shortcut={isLead ? "approve" : undefined}
              disabled={blocked || busy}
              onClick={props.approveOnly}
            >
              <CheckIcon size={16} /> Approve only, I&apos;ll send it in Bison
              {isLead && <kbd className="lcr-kbd">A</kbd>}
            </button>
          )}
        </div>
      )}
      {QUEUE_WARNING[phase.kind] && (
        <p className="lcr-send-warn" role="alert">
          {QUEUE_WARNING[phase.kind]}
        </p>
      )}
      {note && (
        <p className="lcr-meta-text" role="status">
          {note}
        </p>
      )}
    </div>
  );
}
